import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSaveContract } from "../../hooks/useSaveContract";
import { useUpdateContract } from "../../hooks/useUpdateContract";

import { useNavigate } from "@tanstack/react-router";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

export const ContractCardForm = ({ contract, newContract }) => {
  const [viewMode, setViewMode] = useState(!newContract);
  const navigate = useNavigate();

  let defaultValues = {};

  if (!newContract) {
    defaultValues = {
      no: contract.no,
      contractCode: contract.contractCode,
      description: contract.description,
      blocked: contract.blocked,
    };
  }

  if (newContract) {
    defaultValues = {
      no: "",
      contractCode: "",
      description: "",
      blocked: "No",
    };
  }

  const { handleSubmit, reset, control } = useForm({ defaultValues });

  const resetValues = {
    contractCode: "",
    description: "",
    blocked: "No",
  };

  const validationHelper = {
    contractCode: { required: "Contract Code is required" },
    description: { required: "Description is required" },
    blocked: { required: "Blocked is required" },
  };

  // Save Contract Mutation
  const { mutate: saveContract, isSuccess: saveIsSuccess } = useSaveContract();

  // Update Contract Mutation
  const { mutate: updateContract, isSuccess: updateIsSuccess } =
    useUpdateContract();

  // After saving a new contract navigate to the contracts list
  useEffect(() => {
    if (saveIsSuccess) {
      navigate({ to: "/contracts" });
    }
  }, [saveIsSuccess]);

  const handleOnSubmit = (data) => {
    if (newContract) {
      // Add new Contract
      saveContract({
        list: {
          filter: { f_Id: null, f_selectedType: null },
          contract: {
            no: data.contractCode,
            description: data.description,
            contractCode: data.contractCode,
            blocked: data.blocked,
          },
        },
      });
    } else {
      // Update existing contract
      updateContract({
        list: {
          filter: { f_Id: data.no, f_selectedType: null },
          contract: {
            no: data.no,
            description: data.description,
            contractCode: data.contractCode,
            blocked: data.blocked,
          },
        },
      });
    }
    setViewMode(true);
  };

  const handleReset = () => {
    reset(resetValues);
  };

  const handleEditMode = () => {
    setViewMode(false);
  };

  const ContractCodeInput = () => {
    return (
      <Controller
        name="contractCode"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Contract Code"
            error={error !== undefined}
            helperText={error ? validationHelper.contractCode[error.type] : ""}
            disabled={viewMode}
          />
        )}
      />
    );
  };

  const DescriptionInput = () => {
    return (
      <Controller
        name="description"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label="Description"
            error={error !== undefined}
            helperText={error ? validationHelper.description[error.type] : ""}
            disabled={viewMode}
          />
        )}
      />
    );
  };

  const BlockedSelect = () => {
    return (
      <Controller
        name="blocked"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl
            fullWidth
            error={error ? true : false}
            disabled={viewMode}
          >
            <InputLabel id="blocked-label">Blocked</InputLabel>
            <Select
              labelId="blocked-label"
              {...field}
              label="Blocked"
              error={error !== undefined}
            >
              <MenuItem value={"Yes"}>Yes</MenuItem>
              <MenuItem value={"No"}>No</MenuItem>
            </Select>
            {error ? (
              <FormHelperText>
                {validationHelper.blocked[error.type]}
              </FormHelperText>
            ) : null}
          </FormControl>
        )}
      />
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        maxWidth: 800,
        mx: "auto",
      }}
    >
      <Card>
        <Stack
          component="form"
          spacing={2}
          autoComplete="off"
          onSubmit={handleSubmit(handleOnSubmit)}
          sx={{ p: 3 }}
        >
          <ContractCodeInput />
          <DescriptionInput />
          <BlockedSelect />

          {viewMode ? (
            <Button type="button" variant="outlined" onClick={handleEditMode}>
              Edit
            </Button>
          ) : (
            <>
              <Button type="button" variant="outlined" onClick={handleReset}>
                Clear Form
              </Button>
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </>
          )}
        </Stack>
      </Card>
    </Box>
  );
};
